// Morning Briefing — the pieces around the rows: progress ring, week strip, foldable section with supplier groups.
import React, { useState } from 'react';
import { ItemRow } from './BriefingRow';
import { IcChev } from './briefingIcons';
import cs from './briefing.module.css';
import styles from './briefingRows.module.css';

export function Ring({ done, total }) {
  const r = 16; const c = 2 * Math.PI * r; const f = total ? done / total : 0;
  return (
    <div className={cs.ring} data-test="briefing-ring">
      <svg viewBox="0 0 40 40" aria-hidden="true">
        <circle cx="20" cy="20" r={r} className={cs.ringBg} />
        <circle cx="20" cy="20" r={r} className={cs.ringFg} strokeDasharray={`${(c * f).toFixed(1)} ${c.toFixed(1)}`} transform="rotate(-90 20 20)" />
        <text x="20" y="23.4" textAnchor="middle">{Math.round(f * 100)}%</text>
      </svg>
      <div className={cs.ringText}><b>{done} of {total} handled</b><span>Saved when you close</span></div>
    </div>
  );
}

/** Late + Mon..Sun, slim, the same width as the list below. */
export function Week({ week, todayKey, onLate }) {
  return (
    <div className={cs.week} data-test="briefing-week">
      <button type="button" className={`${cs.wk} ${cs.wkLate}`} onClick={onLate} title="Go to late deliveries"><i>Late</i><b>{week.late || '–'}</b></button>
      {week.days.map((d) => (
        <div key={d.key} className={`${cs.wk}${d.today ? ' ' + cs.wkToday : ''}${d.key < todayKey ? ' ' + cs.wkPast : ''}`}>
          <i>{d.today ? 'Today' : <>{d.name}<em> {d.date}</em></>}</i>
          <b className={d.n ? '' : cs.zero}>{d.n || '–'}</b>
        </div>
      ))}
    </div>
  );
}

const CAP = 5;                       // rows shown before "Show N more"; one hidden row is never worth a button
const word = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

function Rows({ items, rowFor }) {
  const [all, setAll] = useState(false);
  const show = all || items.length <= CAP + 1;
  return (
    <>
      {(show ? items : items.slice(0, CAP)).map(rowFor)}
      {!show ? <button type="button" className={styles.more} onClick={() => setAll(true)}>Show {items.length - CAP} more<IcChev /></button> : null}
    </>
  );
}

function Group({ g, handledSet, rowFor }) {
  const [closed, setClosed] = useState(false);
  const done = g.items.filter((i) => handledSet.has(i.id)).length;
  return (
    <>
      <button type="button" className={styles.grp} onClick={() => setClosed(!closed)} aria-expanded={!closed}>
        <span className={styles.g1}><span className={styles.gsup}>{g.supplier}</span></span>
        <span className={styles.g2}>{g.items.length} late</span>
        <span className={styles.g3}>{done ? `${done} handled` : ''}</span>
        <span className={styles.g4}><span>Worst</span>{word(g.worst, 'day')}</span>
        <span className={styles.g5}><span className={styles.chev}><IcChev /></span></span>
      </button>
      {closed ? null : <Rows items={g.items} rowFor={rowFor} />}
    </>
  );
}

const NOTES = { fyi: 'Just so you know. Nothing to do.' };
const SEC_TONE = { late: 'hot', today: 'today', attention: 'info', mentions: 'reply', soon: 'soon', fyi: 'fyi' };

/** A foldable section. Late deliveries come grouped by supplier; the rest as one white list. */
export function Section({ s, handledSet, actions, photos }) {
  const [open, setOpen] = useState(true);
  const rowFor = (i) => (
    <ItemRow key={i.id} item={i} handled={handledSet.has(i.id)} photo={photos.get(i.model)}
      onOpen={() => actions.open(i)} onDone={() => actions.done(i)} onSnooze={() => actions.snooze(i)} onUndo={() => actions.undo(i)} onZoom={actions.zoom} />
  );
  const done = s.items.length - s.open;
  const pct = Math.round((done / s.items.length) * 100);
  const tone = cs['t_' + (SEC_TONE[s.k] || 'fyi')];
  return (
    <section className={`${styles.sec} ${tone}`} id={`briefing-sec-${s.k}`} data-test={`briefing-sec-${s.k}`}>
      <button type="button" className={styles.SH} onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className={cs.dot} />
        <span className={styles.t}>{s.label}</span>
        <span className={styles.n}>{s.items.length}</span>
        {NOTES[s.k] ? <span className={styles.note}>{NOTES[s.k]}</span> : null}
        <span className={styles.prog}>
          <span>{done} of {s.items.length} handled</span>
          <span className={styles.bar}><i style={{ width: `${pct}%` }} /></span>
          <span className={styles.chev}><IcChev /></span>
        </span>
      </button>
      {open ? (
        <div className={styles.L}>
          {s.bySupplier ? s.bySupplier.map((g) => <Group key={g.supplier} g={g} handledSet={handledSet} rowFor={rowFor} />) : <Rows items={s.items} rowFor={rowFor} />}
        </div>
      ) : null}
    </section>
  );
}
